import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { withSelectorSwitch } from 'shared/components/data-display/editors';
import { dateString, wrapList } from 'shared/utils';
import { Title } from 'shared/components/abstract/interactables';
import DataTable from 'shared/components/table/DataTable';
import { macEditCheck, searchMacs } from 'features/tenant/api/macs';
import { useRequest } from 'shared/hooks';
import { buildingEntity, nasEntity } from 'shared/entityDetails';
import { checkPermissions } from 'shared/api/auth';
import { withAsyncSelect } from 'shared/components/data-display/withSelectors';
import { tenantRadiusCheck } from 'shared/api/tenant-radius';
import { deviceSearchPath, getTenantPath } from 'shared/routes/tenant';
import DeviceModal from './DeviceModal';
import { formatTenantName } from '../../shared/utils/formatters';
import { FILTER_DISPLAY_TYPE } from '../../shared/constants';

const filters = [
  {
    label: 'Building',
    param: 'building_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withAsyncSelect(buildingEntity),
    entity: buildingEntity,
  },
  {
    label: 'Nas',
    param: 'nas_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withAsyncSelect(nasEntity),
    entity: nasEntity,
  },
  {
    label: 'Public?',
    param: 'public',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withSelectorSwitch(),
  },
];

export default function DeviceSearch() {
  const [permissions, setPermissions] = useState({});
  const history = useHistory();
  const { search } = useLocation();
  const { request } = useRequest();
  const [forceUpdate, setForceUpdate] = useState(0);

  // Check if logged in user has permission to edit device data.
  useEffect(() => {
    (async () => {
      const response = await request(checkPermissions([macEditCheck, tenantRadiusCheck]));
      if (response) {
        setPermissions({
          editEnabled: response[0],
          radiusDataAccessPermission: response[1],
        });
      }
    })();
  }, [request]);

  const deviceId = parseInt(new URLSearchParams(search).get('id'), 10) || 0;

  const setActiveDevice = (newId) => {
    const searchParams = new URLSearchParams(search);

    if (newId < 1) {
      searchParams.delete('id');
    } else {
      searchParams.set('id', newId);
    }
    history.push(`?${searchParams.toString()}`);
  };
  const deviceFormatter = (device, _, i, rows) => (
    <DeviceModal
      active={device.id === deviceId}
      radiusDataAccessPermission={permissions.radiusDataAccessPermission}
      changeActiveDevice={(n) => setActiveDevice(wrapList(rows, i, n).id)}
      device={device}
      forceUpdate={() => setForceUpdate(forceUpdate + 1)}
      hasMore={rows.length > 1}
      editEnabled={permissions.editEnabled}
      setActiveDevice={setActiveDevice}
      tenant={device.tenant || {}}
    />
  );

  const macFormatter = (mac) => (
    mac.tenant
      ? <Link to={getTenantPath(mac.tenant_id)}>{formatTenantName(mac.tenant)}</Link>
      : 'Not found'
  );

  const columns = [
    {
      name: 'Mac',
      jsonKey: 'mac',
      formatter: deviceFormatter,
    },
    {
      name: 'Tenant',
      formatter: macFormatter,
      orderBy: 'it_firstname',
    },
    {
      name: 'Name',
      jsonKey: 'name',
      orderBy: 'name',
    },
    {
      name: 'Created',
      formatter: (row) => dateString(row.created_at),
      orderBy: 'created_at',
    },
  ];

  return (
    <>
      <Title title="Search Devices" titleLink={deviceSearchPath} />
      <DataTable
        baseSearch="expand=tenant"
        columns={columns}
        filters={filters}
        searchRequest={searchMacs}
        ignoredKeys={['id']}
        rowId="id"
        updateCount={forceUpdate}
        className="table-height-large"
      />
    </>
  );
}
